import { useState } from 'react';
import { useRouteError } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, Stack, IconButton, Tooltip } from '@mui/material';
import { RefreshOutlined, ContentCopy, Check } from '@mui/icons-material';

// Import version from package.json
import packageJson from '../../../package.json';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

// Styled components
const LogoImage = {
  width: 'auto',
  height: 'auto',
  transform: 'scale(1.7)',
  mb: 2.5,
};

const ErrorTitle = {
  color: '#3B4797',
};

const ErrorDescription = {
  fontStyle: 'bold',
  mt: 1,
  mb: 3,
};

const ShowDetailsLink = {
  color: '#1976d2',
  cursor: 'pointer',
  textDecoration: 'underline',
  mt: 3,
  fontSize: '14px',
  fontWeight: 'bold',
};

const ErrorDetailsContainer = {
  mt: 2,
  width: '100%',
  position: 'relative',
};

const CopyButtonContainer = {
  position: 'absolute',
  top: 10,
  right: -20,
  zIndex: 1,
};

const CopyButton = (copied: boolean) => ({
  backgroundColor: copied ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    backgroundColor: copied ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 255, 255, 0.9)',
  },
});

const ErrorCodeBlock = {
  p: 2,
  backgroundColor: '#1e1e1e',
  borderRadius: 1,
  width: '100%',
  fontFamily: 'monospace',
  fontSize: '12px',
  color: '#d4d4d4',
  whiteSpace: 'pre-wrap',
  textAlign: 'left',
  border: '1px solid #333',
  overflow: 'auto',
  maxHeight: '500px',
  minHeight: '200px',
};

export function UnhandleError() {
  const error = useRouteError() as Error;
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const { selectedTenant } = useSelector((state: RootState) => state.auth);

  const handleOnRefresh = () => {
    window.location.reload();
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const copyToClipboard = async () => {
    const errorDetails = `UTC Date: ${new Date().toISOString()}
Client Id: ${selectedTenant.tenant.id}
Client Version: ${packageJson.version}
Client URL: ${window.location.href}

err: ${error?.message || 'Unknown error occurred'}
esrc: Script
et: ClientError
estack: ${error?.stack || 'No stack trace available'}
${error instanceof Error ? `Error Type: ${error.constructor.name}` : ''}`;

    try {
      await navigator.clipboard.writeText(errorDetails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
      minHeight='100vh'
      width='100%'
    >
      <Box component='img' src='/images/main-logo.svg' alt='404' sx={LogoImage} />
      <Stack
        direction='column'
        alignItems='flex-start'
        justifyContent='center'
        mt={3}
        sx={{ width: '100%', maxWidth: '600px' }}
      >
        <Typography variant='h5' component='h2' mt={3} sx={ErrorTitle}>
          Something went wrong.
        </Typography>
        <Typography variant='body1' sx={ErrorDescription}>
          Please try the recommended action below.
        </Typography>
        <Button variant='secondary-table-panel' startIcon={<RefreshOutlined />} onClick={handleOnRefresh}>
          Refresh the application
        </Button>

        <Typography variant='body2' sx={ShowDetailsLink} onClick={toggleDetails}>
          {showDetails ? 'Fewer Details' : 'Show Details'}
        </Typography>

        {showDetails && (
          <Box sx={ErrorDetailsContainer}>
            <Box sx={CopyButtonContainer}>
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton size='small' onClick={copyToClipboard} sx={CopyButton(copied)}>
                  {copied ? <Check fontSize='small' /> : <ContentCopy fontSize='small' />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={ErrorCodeBlock}>
              {`UTC Date: ${new Date().toISOString()}
Client Id: ${selectedTenant.tenant.id}
Client Version: ${packageJson.version}
Client URL: ${window.location.href}


err: ${error?.message || 'Unknown error occurred'}
esrc: Script
et: ClientError
estack: ${error?.stack || 'No stack trace available'}
${error instanceof Error ? `Error Type: ${error.constructor.name}` : ''}`}
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
